import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import { LineChart, DurationSelector, type DateRange } from "../../../../shared/components";
import { usePersistedDateRange } from "../../../../shared/hooks";
import { useUsageStats } from "../../../../shared/hooks";
import { useClientScope } from "../../../../shared/contexts";

const StatBox: React.FC<{
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}> = ({ label, value, icon, color }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      p: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      height: "100%",
    }}
  >
    <Box sx={{ color, display: "flex" }}>{icon}</Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>
        {value.toLocaleString()}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Box>
);

export const UsageTab: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId } = useClientScope();
  const { dateRange, setDateRange } = usePersistedDateRange();

  const [startDate, endDate] = dateRange;
  const usageQuery = useUsageStats({
    start_date: startDate ? startDate.format("YYYY-MM-DD") : undefined,
    end_date: endDate ? endDate.format("YYYY-MM-DD") : undefined,
    client_id: effectiveClientId,
  });

  const stats = usageQuery.data?.data;

  return (
    <SettingsSection>
      <Alert severity="info">
        Mobile app usage for the selected period — app opens and how many distinct
        users were active. Sourced from app-open / foreground events.
      </Alert>

      <SectionCard
        title="Mobile usage"
        action={
          <DurationSelector
            value={dateRange}
            onChange={(range: DateRange) => setDateRange(range)}
            buttonVariant="outlined"
          />
        }
      >
        {usageQuery.isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : usageQuery.error ? (
          <Alert severity="error">Failed to load usage data. Please try again.</Alert>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <StatBox
                  label="App opens"
                  value={stats?.total_opens ?? 0}
                  icon={<PhoneIphoneIcon />}
                  color={theme.palette.info.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <StatBox
                  label="Active users"
                  value={stats?.active_users ?? 0}
                  icon={<PeopleAltIcon />}
                  color={theme.palette.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <StatBox
                  label="Active days"
                  value={stats?.active_days ?? 0}
                  icon={<EventAvailableIcon />}
                  color={theme.palette.warning.main}
                />
              </Grid>
            </Grid>

            {stats && stats.daily.length > 0 ? (
              <LineChart
                data={stats.daily as unknown as Array<Record<string, string | number>>}
                xAxisKey="label"
                series={[
                  {
                    dataKey: "opens",
                    label: "App opens",
                    color: theme.palette.info.main,
                    area: true,
                  },
                  {
                    dataKey: "active_users",
                    label: "Active users",
                    color: theme.palette.success.main,
                  },
                ]}
                height={320}
                yAxisMin={0}
              />
            ) : (
              <Alert severity="info">No usage recorded for this period.</Alert>
            )}
          </Stack>
        )}
      </SectionCard>
    </SettingsSection>
  );
};
